import { SPAWN_POSITION, BODEGA_EXTERIOR_STANDPOINT, ROOFTOP, SUBWAY_LANDING, TUNNEL_FLOOR, EL_STATION_CHECKPOINT_MARKER } from './layout';

export interface Checkpoint {
  id: string;
  label: string;
  /** Where death respawns the player if this is the currently-active checkpoint. */
  respawnX: number;
  respawnZ: number;
  /** The proximity circle that activates this checkpoint on approach — omitted for `spawn`, which is active from frame 1 and never re-triggers. */
  trigger?: { x: number; z: number; radius: number };
  /**
   * Wave E (PLAN-CITY3 §6): mirrors `world/interact.ts`'s own `Interactable.
   * requireY` exactly — optional and additive, every pre-Wave-E checkpoint
   * (all ground-level) is byte-unaffected. Needed here because the el
   * platform is the first checkpoint whose real usable XZ ALSO sits above
   * open, walkable STREET-level space (the deck runs directly over the main
   * street) — without this, a street-level pedestrian at the same XZ would
   * spuriously "discover" a checkpoint they never climbed the stair to
   * reach, and could even become its active respawn target.
   */
  requireY?: { y: number; tolerance: number };
}

/**
 * S05's checkpoint/respawn system (PLAN-CITY §5). Six waypoints across the
 * block — the same six `MARKERS` layout.ts already seeded — become real
 * checkpoints: reaching one for the first time both banners it (main.ts's
 * "discovered" toast, the exploration-pillar payoff Karan's own G1 words
 * called out — "just wanted to see everything") and becomes where death
 * respawns the player, replacing S02's hardcoded always-bodega-door
 * behavior. `spawn` is the one exception: always active by default (no
 * trigger, no discovery toast — it's where the run already started, not
 * something to "discover"), so a player who dies before ever reaching the
 * bodega respawns where they actually began, not at a location on the far
 * side of the intersection they may not have visited yet.
 *
 * Trigger radii are hand-checked against layout.ts's own named bounds
 * (ROOFTOP/SUBWAY_LANDING/TUNNEL_FLOOR) the same way `ai/habitats.ts`'s own
 * comment warns every future area check must be — each one sits with a
 * real margin inside its region's own half-extent along the tighter axis
 * (review finding: an earlier draft used subway_landing radius 2.2 against
 * SUBWAY_LANDING's 2.0m Z half-extent, and utility_tunnel radius 3 exactly
 * tangent to TUNNEL_FLOOR's 3.0m X half-extent — both technically bled
 * outside or sat with zero margin, contradicting this same comment's
 * original claim; both are now sized with margin instead).
 */
export const CHECKPOINTS: Checkpoint[] = [
  { id: 'spawn', label: 'SPAWN', respawnX: SPAWN_POSITION.x, respawnZ: SPAWN_POSITION.z },
  {
    id: 'bodega',
    label: 'BODEGA',
    respawnX: BODEGA_EXTERIOR_STANDPOINT.x,
    respawnZ: BODEGA_EXTERIOR_STANDPOINT.z,
    trigger: { x: BODEGA_EXTERIOR_STANDPOINT.x, z: BODEGA_EXTERIOR_STANDPOINT.z, radius: 3 },
  },
  {
    id: 'rooftop',
    label: 'ROOFTOP',
    respawnX: (ROOFTOP.minX + ROOFTOP.maxX) / 2,
    respawnZ: 17,
    trigger: { x: (ROOFTOP.minX + ROOFTOP.maxX) / 2, z: 17, radius: 3 },
  },
  {
    id: 'park_pocket',
    label: 'PARK POCKET',
    respawnX: -17,
    respawnZ: 17,
    trigger: { x: -17, z: 17, radius: 4 },
  },
  {
    id: 'subway_landing',
    label: 'SUBWAY LANDING',
    respawnX: (SUBWAY_LANDING.minX + SUBWAY_LANDING.maxX) / 2,
    respawnZ: -16,
    // radius 1.7: SUBWAY_LANDING's own Z half-extent is exactly 2.0m
    // (minZ −18, maxZ −14, centered at z=−16) — 1.7 leaves a real 0.3m
    // margin on both ends instead of the 2.2 an earlier draft used, which
    // overshot by 0.2m into the open SUBWAY_STAIR entrance just north of it.
    trigger: { x: (SUBWAY_LANDING.minX + SUBWAY_LANDING.maxX) / 2, z: -16, radius: 1.7 },
  },
  {
    id: 'utility_tunnel',
    label: 'UTILITY TUNNEL',
    respawnX: (TUNNEL_FLOOR.minX + TUNNEL_FLOOR.maxX) / 2,
    respawnZ: 34,
    // radius 2.6: TUNNEL_FLOOR's own X half-extent is exactly 3.0m (minX
    // 14, maxX 20) — 2.6 leaves a real 0.4m margin instead of the 3 an
    // earlier draft used, which was exactly tangent to both edges (zero
    // margin, not "safely inside" as this file's own header comment claims).
    trigger: { x: (TUNNEL_FLOOR.minX + TUNNEL_FLOOR.maxX) / 2, z: 34, radius: 2.6 },
  },
  {
    id: 'el_station',
    label: 'THE STATION',
    respawnX: EL_STATION_CHECKPOINT_MARKER.x,
    respawnZ: EL_STATION_CHECKPOINT_MARKER.z,
    // radius 2.2 matches `bodega`'s own generous circle — the station-stair
    // gap in EL_DECK_RAILS (x26.5-29.5) is the only nearby feature, and this
    // marker sits centered inside it with real margin either side.
    trigger: { x: EL_STATION_CHECKPOINT_MARKER.x, z: EL_STATION_CHECKPOINT_MARKER.z, radius: 2.2 },
    // See this interface's own `requireY` comment above — REQUIRED here,
    // not optional: tolerance 1.5 comfortably covers a real jump/vault near
    // the platform edge while excluding street level (y≈0-1.4) by a margin
    // of ~5.5m, nowhere close to ambiguous.
    requireY: { y: EL_STATION_CHECKPOINT_MARKER.y, tolerance: 1.5 },
  },
];

const SPAWN_CHECKPOINT = CHECKPOINTS[0];

/**
 * Tracks which checkpoint is currently active (respawn target) and which
 * ones have already been discovered (so `update()` only reports a NEWLY
 * reached one once, not on every tick spent standing inside its trigger
 * circle). Deliberately in-memory only, not persisted — S07 owns the real
 * save system; a checkpoint re-discovering itself every reload until then
 * is a fine, explicitly-scoped-out gap (same "the real save is S07's job"
 * precedent T2's `AIM_PRESET_STORAGE_KEY` comment already sets, except
 * this one doesn't even get that narrow localStorage exception — a
 * respawn location silently surviving a reload while every other run-state
 * field doesn't would be a worse inconsistency than just resetting).
 */
export class CheckpointTracker {
  private currentId = SPAWN_CHECKPOINT.id;
  private readonly discovered = new Set<string>([SPAWN_CHECKPOINT.id]);

  get current(): Checkpoint {
    return CHECKPOINTS.find((c) => c.id === this.currentId) ?? SPAWN_CHECKPOINT;
  }

  /** K-2 (PLAN-CITY3 §4): the hunt board's LEADS section gates a den rumor on the player having reached a given checkpoint — a public query the class had no external way to answer before this. */
  hasDiscovered(id: string): boolean {
    return this.discovered.has(id);
  }

  /**
   * S07: restores the active checkpoint from a suspended-save snapshot at
   * boot (`meta/save.ts`'s `SuspendedRun.checkpointId`). Marks it discovered
   * too, WITHOUT going through `update()` — a resume must never re-fire the
   * discovery toast for a checkpoint the player already found last session.
   * An id that doesn't match any real `Checkpoint` (a stale value from a
   * removed checkpoint, or salvageSave's own defensive validation somehow
   * still letting through something unrecognized) is a silent no-op —
   * `current`'s own `?? SPAWN_CHECKPOINT` fallback already makes an
   * unrecognized id safe, so there's nothing to fix, just nothing to apply.
   */
  restoreDiscovered(id: string): void {
    const found = CHECKPOINTS.find((c) => c.id === id);
    if (!found) return;
    this.currentId = found.id;
    this.discovered.add(found.id);
  }

  /**
   * Call once per tick with the player's live position. `playerY` is
   * optional — genuinely omittable, matching `InteractSystem.update()`'s own
   * established convention (world/interact.ts) — every pre-Wave-E call site
   * keeps working unchanged. Any checkpoint with `requireY` set FAILS
   * CLOSED when `playerY` is omitted, the same fails-closed discipline that
   * convention already established. Returns the checkpoint that was just
   * newly activated this call, or null.
   */
  update(playerX: number, playerZ: number, playerY?: number): Checkpoint | null {
    for (const cp of CHECKPOINTS) {
      if (!cp.trigger || this.discovered.has(cp.id)) continue;
      if (cp.requireY && (playerY === undefined || Math.abs(playerY - cp.requireY.y) > cp.requireY.tolerance)) continue;
      const dist = Math.hypot(playerX - cp.trigger.x, playerZ - cp.trigger.z);
      if (dist <= cp.trigger.radius) {
        this.discovered.add(cp.id);
        this.currentId = cp.id;
        return cp;
      }
    }
    return null;
  }
}
